import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment'
import React, { Fragment, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import HomeActivityBox from 'src/components/HomeActivityBox/HomeActivityBox'
import HomepageChart from 'src/components/HomepageChart/HomepageChart'
import HomepageTopMetrics from 'src/components/HomepageTopMetrics/HomepageTopMetrics'
import HomePromotionsStack from 'src/components/HomePromotionsStack/HomePromotionsStack'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectsWithCountsBox from 'src/components/ObjectsWithCountsBox/ObjectsWithCountsBox'
import OrgAdminBox from 'src/components/OrgAdminBox/OrgAdminBox'
import TransactionsOverviewBox from 'src/components/TransactionsOverviewBox/TransactionsOverviewBox'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { ImageNames, OrganizationRoles, svgImageNames } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import {
  useColorOrGrayscaleImage,
  useColorOrGrayscaleSVG,
} from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { TransactionStatus } from 'src/models/transaction.models'
import { UserFeature } from 'src/models/user.models'
import {
  accountsAccountsUrl,
  budgetsUrl,
  myWalletTransactionsURL,
  myWalletVirtualCardsUrl,
  virtualCardsUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { useQueryTransactions } from 'src/queries/transactions.queries'
import { getOrganizationCardApprovalsEnabled } from 'src/redux/actions/data/organizations.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectIsAuthenticatedFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  selectCardApprovalsFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import {
  selectCreditCardsActiveDelegateCountStatisticsFromState,
  selectCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveSourceCountStatisticsFromState,
  selectCreditCardsCountStatisticsFromState,
  selectCreditCardsDelegateCountStatisticsFromState,
  selectCreditCardsDelegateUserMeCountStatisticsFromState,
  selectCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFromState,
  selectCreditCardsPendingSourceUserMeCountStatisticsFromState,
  selectCreditCardsSourceBudgetsEnabledCountStatisticsFromState,
  selectCreditCardsSourceCountStatisticsFromState,
  selectCreditCardsTimedOutSourceCountStatisticsFromState,
  selectVirtualCardsActiveOrPendingRecipientMeCountStatisticsFromState,
  selectVirtualCardsRecipientMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { AccentImageUp } from 'src/style/keyframeUtils'
import { css } from 'styled-components'

const Home: React.FC = () => {
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const {
    results: statisticsQueries,
    anyLoading: reactQueryStatisticsLoading,
  } = useQueryStatistics({
    statisticsKeys: [StatisticsKeys.VirtualCardsActiveCount],
  })
  const virtualCardsActiveCount =
    statisticsQueries?.VirtualCardsActiveCount?.data ?? 0
  const creditCardsSourceBudgetsEnabledCount = useSelector(
    selectCreditCardsSourceBudgetsEnabledCountStatisticsFromState,
  )
  const creditCardsPendingSourceUserMeCount = useSelector(
    selectCreditCardsPendingSourceUserMeCountStatisticsFromState,
  )
  const virtualCardsRecipientMeCount = useSelector(
    selectVirtualCardsRecipientMeCountStatisticsFromState,
  )
  const virtualCardsActiveOrPendingRecipientMeCount = useSelector(
    selectVirtualCardsActiveOrPendingRecipientMeCountStatisticsFromState,
  )
  const creditCardsActiveDelegateCount = useSelector(
    selectCreditCardsActiveDelegateCountStatisticsFromState,
  )
  const creditCardsActiveSourceCount = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )
  const creditCardsTimedOutSourceCount = useSelector(
    selectCreditCardsTimedOutSourceCountStatisticsFromState,
  )
  const creditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCount =
    useSelector(
      selectCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFromState,
    )
  const creditCardsDelegateCount = useSelector(
    selectCreditCardsDelegateCountStatisticsFromState,
  )
  const creditCardsSourceCount = useSelector(
    selectCreditCardsSourceCountStatisticsFromState,
  )
  const creditCardsCount = useSelector(
    selectCreditCardsCountStatisticsFromState,
  )
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const appName = useSelector(selectAppName)
  const remoteConfigLoaded = useSelector(selectLoadedFromRemoteConfigState)
  const creditCardsActiveSourceCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsDelegateUserMeCount = useSelector(
    selectCreditCardsDelegateUserMeCountStatisticsFromState,
  )
  const user = useSelector(selectUserListModelFromState)

  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const dispatch = useBackgroundDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const topMetricsStatsLoaded =
    !reactQueryStatisticsLoading &&
    statisticsLoaded.creditCardsActiveDelegateCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded &&
    statisticsLoaded.creditCardsPendingSourceUserMeCountStatisticsLoaded &&
    statisticsLoaded.creditCardsCountStatisticsLoaded &&
    statisticsLoaded.virtualCardsRecipientMeCountStatisticsLoaded

  const relatedToCreditCards = creditCardsCount > 0
  const hasVirtualCards = virtualCardsRecipientMeCount > 0

  const myOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)
  const hasOrgOwnerPermissions = user?.hasOrgOwnerPermissions
  const hasOrgAdminPermissions = user?.hasOrgAdminPermissions
  const isGuestUser =
    user?.organizationRole === OrganizationRoles.guest && showGuestRole

  const {
    ORGANIZATION_USER_STATISTICS: canViewOrganizationUserStatistics,
    REGISTER_ACCOUNT: canRegisterAccount,
    VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures,
  } = useQueryUserFeatures([
    UserFeature.ORGANIZATION_USER_STATISTICS,
    UserFeature.REGISTER_ACCOUNT,
    UserFeature.VIEW_VIRTUAL_CARD_FEATURES,
  ]).data || {}

  const hasFullOrgBookkeeperPermissions =
    canViewOrganizationUserStatistics || user?.hasOrgAdminPermissions
  const homescreenTopMetricsAccent = useColorOrGrayscaleSVG(
    svgImageNames.HomescreenTopMetricsAccent,
  )
  const homescreenOrgAccent = useColorOrGrayscaleImage(
    ImageNames.HomescreenOrgAccent,
  )
  const homescreenAccountAccent = useColorOrGrayscaleImage(
    ImageNames.HomescreenAccountAccent,
  )
  const homescreenCardAccent = useColorOrGrayscaleImage(
    ImageNames.HomescreenCardAccent,
  )

  // transactions
  const {
    data: transactionsData,
    isFetching: transactionsLoading,
    complete: transactionsComplete,
  } = useQueryTransactions({
    filters: {
      sort: '-date',
      recipientOrPhysicalCardUserId: user ? [user.id] : undefined,
      status: [
        TransactionStatus.Cleared,
        TransactionStatus.Declined,
        TransactionStatus.Pending,
        TransactionStatus.AuthReversal,
      ],
      since: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      until: moment().format('YYYY-MM-DD'),
    },
    enabled:
      isAuthenticated &&
      (isGuestUser || (hasVirtualCards && !relatedToCreditCards)),
    perPage: 3,
  })

  useEffect((): void => {
    const topMetricsKeys = [
      appStatisticsKeys.CreditCardsActiveDelegateCountStatistics,
      appStatisticsKeys.CreditCardsActiveSourceCountStatistics,
      appStatisticsKeys.CreditCardsPendingSourceUserMeCountStatistics,
      appStatisticsKeys.CreditCardsCountStatistics,
      appStatisticsKeys.VirtualCardsRecipientMeCountStatistics,
    ]

    const nonTopMetricsKeys = [
      appStatisticsKeys.CreditCardsSourceBudgetsEnabledCountStatistics,
      appStatisticsKeys.VirtualCardsPendingRequestCardholderMeCountStatistics,
      appStatisticsKeys.VirtualCardsActiveOrPendingRecipientMeCountStatistics,
      appStatisticsKeys.CreditCardsTimedOutSourceCountStatistics,
      appStatisticsKeys.CreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatistics,
      appStatisticsKeys.CreditCardsDelegateCountStatistics,
      appStatisticsKeys.CreditCardsSourceCountStatistics,
      appStatisticsKeys.CreditCardsUserOrViewerMeCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
    ]

    if (remoteConfigLoaded) {
      dispatchStatistics(topMetricsKeys)

      if (!hasFullOrgBookkeeperPermissions) {
        dispatchStatistics(nonTopMetricsKeys)
      }
    }
  }, [dispatchStatistics, hasFullOrgBookkeeperPermissions, remoteConfigLoaded])

  useEffect(() => {
    if (user?.organizationId !== undefined && cardApprovalRules === undefined) {
      dispatch(getOrganizationCardApprovalsEnabled(user.organizationId))
    }
  }, [user])

  const yourAccountsBudgets = [
    ...(creditCardsSourceCount > 0 && creditCardsActiveSourceCount > 0
      ? [
          {
            id: 'accounts',
            count: creditCardsActiveSourceCount,
            objectName: <Trns source={'Active'} />,
            title: <Trns source={'Accounts'} />,
            to: accountsAccountsUrl(),
            ariaLabelSource: 'Accounts',
          },
        ]
      : []),
    ...(creditCardsSourceCount > 0 && creditCardsActiveSourceCount === 0
      ? [
          {
            id: 'accounts',
            isRed: true,
            objectName:
              creditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCount >
              0 ? (
                <Trns source={'Verification Required'} />
              ) : creditCardsPendingSourceUserMeCount > 0 ? (
                <Trns source={'Action Required'} />
              ) : creditCardsTimedOutSourceCount > 0 ? (
                <Trns source={'Timed Out'} />
              ) : (
                <Trns source={'Blocked'} />
              ),
            title: <Trns source={'Accounts'} />,
            to: accountsAccountsUrl(),
            ariaLabelSource: 'Accounts',
          },
        ]
      : []),
    ...(creditCardsDelegateCount > 0 || creditCardsSourceBudgetsEnabledCount > 0
      ? [
          {
            id: 'budgets',
            count: creditCardsActiveDelegateCount,
            objectName: <Trns source={'Active'} />,
            title: <Trns source={'Budgets'} />,
            to: budgetsUrl(),
            ariaLabelSource: 'Budgets',
          },
        ]
      : []),
    ...(creditCardsDelegateCount > 0 ||
    creditCardsSourceCount > 0 ||
    virtualCardsActiveCount > 0
      ? [
          {
            id: 'virtualCards',
            objectName: <Trns source={'Active'} />,
            count: virtualCardsActiveCount,
            title: <Trns source={'Virtual Cards'} />,
            to: virtualCardsUrl(),
            ariaLabelSource: 'Virtual Cards',
          },
        ]
      : []),
  ]

  const borderGutterSMUpValue = parseInt(
    styleConstants.heights.borderGutterSMUp.replace('px', ''),
  )
  const borderGutterXXMDownValue = parseInt(
    styleConstants.heights.borderGutterXXMDown.replace('px', ''),
  )

  return (
    <>
      <Helmet>
        <title>{`Home | ${appName}`}</title>
        <meta name="description" content={`Home page of ${appName}`} />
      </Helmet>
      <BackgroundPage>
        {remoteConfigLoaded && topMetricsStatsLoaded ? (
          <BackgroundPageContent
            css={css`
              padding: ${borderGutterSMUpValue}px
                ${styleConstants.widths.borderGutterLGUp};
              ${styleConstants.heights.borderGutterSMUp};

              ${getBreakpointOnly(breakpointSizes.MD)(css`
                padding: ${borderGutterSMUpValue}px
                  ${styleConstants.widths.borderGutterMDOnly}
                  ${styleConstants.heights.borderGutterSMUp};
              `)}

              ${getBreakpointOnly(breakpointSizes.SM)(css`
                padding: ${borderGutterSMUpValue}px
                  ${styleConstants.widths.borderGutterSMOnly}
                  ${styleConstants.heights.borderGutterSMUp};
              `)}

            ${getBreakpointDown(breakpointSizes.XSM)(css`
                padding: ${borderGutterXXMDownValue}px
                  ${styleConstants.widths.borderGutterXSMDown}
                  ${styleConstants.heights.borderGutterXXMDown};
              `)}
            `}
          >
            <div
              data-testid="welcome-back-message"
              css={css`
                margin-top: 20px;
                padding-left: 40px;
                display: flex;
                flex-direction: column;

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  margin-bottom: 10px;
                  padding-left: 20px;
                `)}
              `}
            >
              {hasFullOrgBookkeeperPermissions ? (
                <div
                  css={css`
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                >
                  <Trns
                    source={'Welcome back to ${orgName},'}
                    expressions={{
                      orgName: myOrganization?.name || '',
                    }}
                  />
                  <div
                    css={css`
                      margin-top: 4px;
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {user?.firstNameOrEmail}
                  </div>
                </div>
              ) : (
                <>
                  <span
                    css={css`
                      ${getFontCss(
                        40,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.regular,
                      )}

                      ${getBreakpointDown(breakpointSizes.XSM)(css`
                        ${getFontCss(
                          24,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )}
                      `)}
                    `}
                  >
                    {!relatedToCreditCards && !hasVirtualCards ? (
                      <Trns source={'Welcome,'} />
                    ) : (
                      (relatedToCreditCards || hasVirtualCards) && (
                        <Trns source={'Welcome back,'} />
                      )
                    )}
                  </span>
                  <span
                    className={'user-name'}
                    css={css`
                      ${getFontCss(
                        40,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}

                      ${getBreakpointDown(breakpointSizes.XSM)(css`
                        ${getFontCss(
                          24,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `)}
                    `}
                  >
                    {user?.firstNameOrEmail}
                  </span>
                </>
              )}
            </div>
            {isGuestUser ? (
              <HorizontalContentCollection
                css={css`
                  height: 100%;
                `}
                splitLeft
              >
                <ObjectsWithCountsBox
                  objects={[
                    {
                      id: 'yourCards',
                      objectName: <Trns source={'Active & Pending'} />,
                      count: virtualCardsActiveOrPendingRecipientMeCount,
                      title: <Trns source={'Your Cards'} />,
                      to: myWalletVirtualCardsUrl(),
                      ariaLabelSource: 'Your Cards',
                    },
                  ]}
                />
                <TransactionsOverviewBox
                  title={<Trns source={'Recent Transactions'} />}
                  transactions={transactionsData?.transactions ?? []}
                  transactionsLoading={transactionsLoading}
                  transactionsComplete={transactionsComplete}
                  navAway={{
                    label: <Trns source={'View Your Transactions'} />,
                    to: {
                      pathname: myWalletTransactionsURL(),
                      search: '',
                      hash: '',
                      state: {},
                    },
                    ariaLabelSource: 'View Your Transactions',
                  }}
                />
              </HorizontalContentCollection>
            ) : (
              <HorizontalContentCollection
                css={css`
                  height: 100%;
                `}
                leftWidthPercentage={66}
              >
                <VerticalContentCollection
                  css={css`
                    height: 100%;
                    position: relative;
                  `}
                >
                  {hasFullOrgBookkeeperPermissions ? (
                    <>
                      {hasVirtualCardFeatures && (
                        <HomepageTopMetrics
                          activeVirtualCards={virtualCardsActiveCount}
                          activeVirtualCardsLoaded={
                            !reactQueryStatisticsLoading
                          }
                          activeBudgets={creditCardsActiveDelegateCount}
                          activeBudgetsLoaded={
                            statisticsLoaded.creditCardsActiveDelegateCountStatisticsLoaded
                          }
                          activeAccounts={creditCardsActiveSourceCount}
                          activeAccountsLoaded={
                            statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded
                          }
                          actionRequired={
                            creditCardsPendingSourceUserMeCount > 0
                          }
                        />
                      )}
                      <HomepageChart
                        creditCardsCountLoaded={
                          statisticsLoaded.creditCardsCountStatisticsLoaded
                        }
                        relatedToCreditCards={relatedToCreditCards}
                      />
                    </>
                  ) : (
                    <>
                      {(hasFullOrgBookkeeperPermissions ||
                        creditCardsSourceCount > 0 ||
                        creditCardsDelegateCount > 0) && (
                        <VerticalContentCollection
                          css={css`
                            position: relative;
                            box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 8px 0px;
                            border-radius: 10px;
                          `}
                        >
                          {hasFullOrgBookkeeperPermissions && (
                            <OrgAdminBox
                              css={css`
                                box-shadow: none;
                              `}
                              orgName={myOrganization?.name}
                              subtitle={<Trns source={'Your Organization'} />}
                              orgBookkeeper={hasFullOrgBookkeeperPermissions}
                              topBox={
                                creditCardsSourceCount > 0 ||
                                creditCardsDelegateCount > 0
                              }
                            />
                          )}
                          {(creditCardsSourceCount > 0 ||
                            creditCardsDelegateCount > 0) && (
                            <ObjectsWithCountsBox
                              css={css`
                                box-shadow: none;
                              `}
                              objects={yourAccountsBudgets}
                              orgBookkeeper={hasFullOrgBookkeeperPermissions}
                              bottomBox={hasFullOrgBookkeeperPermissions}
                            />
                          )}
                        </VerticalContentCollection>
                      )}
                      {hasVirtualCards ? (
                        <Fragment>
                          <ObjectsWithCountsBox
                            objects={[
                              {
                                id: 'yourCards',
                                objectName: (
                                  <Trns source={'Active & Pending'} />
                                ),
                                count:
                                  virtualCardsActiveOrPendingRecipientMeCount,
                                title: <Trns source={'Your Cards'} />,
                                to: myWalletVirtualCardsUrl(),
                                ariaLabelSource: 'Your Cards',
                              },
                            ]}
                          />
                          {!relatedToCreditCards && (
                            <TransactionsOverviewBox
                              title={<Trns source={'Recent Transactions'} />}
                              transactions={
                                transactionsData?.transactions ?? []
                              }
                              transactionsLoading={transactionsLoading}
                              transactionsComplete={transactionsComplete}
                              navAway={{
                                label: (
                                  <Trns source={'View Your Transactions'} />
                                ),
                                to: {
                                  pathname: myWalletTransactionsURL(),
                                  search: '',
                                  hash: '',
                                  state: {},
                                },
                                ariaLabelSource: 'View Your Transactions',
                              }}
                            />
                          )}
                        </Fragment>
                      ) : (
                        !relatedToCreditCards &&
                        (hasVirtualCardFeatures ? (
                          <HomePromotionsStack
                            hasVirtualCards={hasVirtualCards}
                            relatedToCreditCards={relatedToCreditCards}
                            hasOrgOwnerPermissions={hasOrgOwnerPermissions}
                            hasOrgAdminPermissions={hasOrgAdminPermissions}
                            activeCreditCards={
                              creditCardsActiveSourceCount || 0
                            }
                            canRegisterAccount={canRegisterAccount}
                            hasVirtualCardFeatures={hasVirtualCardFeatures}
                          />
                        ) : (
                          <HomepageChart
                            creditCardsCountLoaded={
                              statisticsLoaded.creditCardsCountStatisticsLoaded
                            }
                            relatedToCreditCards={relatedToCreditCards}
                          />
                        ))
                      )}
                    </>
                  )}
                  {(relatedToCreditCards || hasVirtualCards) && (
                    <div
                      css={css`
                        position: absolute;
                        width: 150px;
                        height: 40px;
                        right: 10px;
                        z-index: -1;
                        top: -60px;
                        overflow: hidden;

                        ${AccentImageUp(0)}

                        ${!!hasFullOrgBookkeeperPermissions &&
                        css`
                          overflow: visible;
                          ${AccentImageUp(40)}
                        `}
                      `}
                    >
                      {hasFullOrgBookkeeperPermissions ? (
                        <div
                          css={css`
                            ${hasFullOrgBookkeeperPermissions &&
                            css`
                              position: absolute;
                              top: -60px;
                            `}
                          `}
                        >
                          {homescreenTopMetricsAccent}
                        </div>
                      ) : (
                        <img
                          css={css`
                            width: 150px;
                            ${!!hasFullOrgBookkeeperPermissions &&
                            css`
                              position: absolute;
                              top: -60px;
                            `}
                          `}
                          src={
                            hasFullOrgBookkeeperPermissions
                              ? homescreenOrgAccent
                              : relatedToCreditCards
                                ? homescreenAccountAccent
                                : homescreenCardAccent
                          }
                          alt={''}
                        />
                      )}
                    </div>
                  )}
                </VerticalContentCollection>
                <VerticalContentCollection>
                  <HomeActivityBox
                    preReqStatsLoaded={topMetricsStatsLoaded}
                    activeBudgets={creditCardsDelegateUserMeCount}
                    activeAccounts={
                      creditCardsActiveSourceCanIssueVirtualCardsCount
                    }
                  />
                </VerticalContentCollection>
              </HorizontalContentCollection>
            )}
          </BackgroundPageContent>
        ) : (
          <LoadingBackgroundPage />
        )}
      </BackgroundPage>
    </>
  )
}

export default Home
